import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  api,
  type PredictionHistoryGame,
  type PredictionHistoryResponse,
  type SlateBettingModel,
  type SlateGame,
  type SlateResponse,
  type SlateResearchModel,
  type SlateWindow,
} from "../lib/api";
import {
  resolveActiveBettingModel,
  resolveBettingFields,
  resolveBettingModels,
} from "../lib/bettingResolver";
import {
  loadRowSelections,
  pruneRowSelections,
  saveRowSelections,
} from "../lib/bettingRowSelection";
import { config } from "../lib/config";
import { useAuth } from "../lib/auth";
import { useDisplayPreferences } from "../lib/displayPreferences";
import { ResultsView } from "../components/ResultsView";

type SlateView = "upcoming" | "results";

function fmtProb(p: number | null | undefined): string {
  if (p == null || Number.isNaN(p)) return "—";
  return `${(p * 100).toFixed(1)}%`;
}

function fmtDelta(d: number | null | undefined): string {
  if (d == null || Number.isNaN(d)) return "—";
  const pct = (d * 100).toFixed(2);
  return d > 0 ? `+${pct}pp` : `${pct}pp`;
}

function americanToImplied(ml: number): number {
  if (ml >= 0) return 100 / (ml + 100);
  return -ml / (-ml + 100);
}

/** Expected value in dollars per $1 staked on the home side (same unit as backend `ev`). */
function fmtEV(ev: number | null | undefined): string {
  if (ev == null || Number.isNaN(ev)) return "—";
  const abs = Math.abs(ev).toFixed(2);
  return ev >= 0 ? `+$${abs}` : `-$${abs}`;
}

function fmtKelly(k: number | null | undefined): string {
  if (k == null || Number.isNaN(k)) return "";
  return `${(k * 100).toFixed(2)}% bk`;
}

type EdgeInfo = { label: string; bg: string; fg: string } | null;

function edgeInfo(edge: string | null | undefined): EdgeInfo {
  if (edge === "strong") {
    return { label: "strong", bg: "rgba(74, 222, 128, 0.15)", fg: "#4ade80" };
  }
  if (edge === "lean") {
    return { label: "lean", bg: "rgba(14, 165, 233, 0.12)", fg: "#38bdf8" };
  }
  return null;
}

function fmtSpread(s: number | null | undefined): string {
  if (s == null || Number.isNaN(s)) return "—";
  const x = Number(s.toFixed(2));
  if (x === 0) return "PK";
  const str = x.toFixed(2);
  return x > 0 ? `+${str}` : str;
}

function fmtTotal(t: number | null | undefined): string {
  if (t == null || Number.isNaN(t)) return "—";
  return Number(t).toFixed(2);
}

function fmtGameDate(raw: string): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatResearchModelHeader(label: string): ReactNode {
  const match = /^Box impact\s+(.+)$/.exec(label);
  if (!match) return label;
  return (
    <>
      Box impact
      <br />
      {match[1]}
    </>
  );
}

function researchHeaderWraps(label: string): boolean {
  return /^Box impact\s+/.test(label);
}

function formatLeagueLabel(leagueId: string): string {
  if (leagueId === "wnba") return "WNBA";
  return leagueId.replace(/_/g, " ").toUpperCase();
}

function fmtIsoDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type SortKey = "date" | "baseline" | "user" | "delta" | "ev" | "gap";

type SlateGameDerived = SlateGame & {
  __baseline_p_home?: number | null;
  __delta_user_vs_baseline?: number | null;
};

/** Calendar YYYY-MM-DD in the browser's local timezone (date input values). */
function localTodayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** Best-effort YYYY-MM-DD from API game_date without timezone skew. */
function gameDateYmd(raw: string): string {
  if (!raw) return "";
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

function clampYmd(ymd: string, min: string, max: string): string {
  if (!ymd) return min;
  if (ymd < min) return min;
  if (ymd > max) return max;
  return ymd;
}

function catalogDateBounds(
  games: SlateGame[] | undefined,
): { min: string; max: string } | null {
  if (!games?.length) return null;
  const ymds = games.map((g) => gameDateYmd(g.game_date)).filter(Boolean);
  if (!ymds.length) return null;
  return {
    min: ymds.reduce((a, b) => (a < b ? a : b)),
    max: ymds.reduce((a, b) => (a > b ? a : b)),
  };
}

function dateRangeMatch(g: SlateGame, start: string, end: string): boolean {
  const y = gameDateYmd(g.game_date);
  if (!y) return false;
  if (start && y < start) return false;
  if (end && y > end) return false;
  return true;
}

function gameDateMs(raw: string): number {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function numOrNull(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return v;
}

function cmpNum(a: number | null, b: number | null, mul: number): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return mul * (a - b);
}

function teamFilterMatch(g: SlateGame, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return (
    g.home_team.toLowerCase().includes(s) ||
    g.away_team.toLowerCase().includes(s)
  );
}

function sortGames(
  games: SlateGameDerived[],
  sortKey: SortKey,
  sortDir: "asc" | "desc",
): SlateGameDerived[] {
  const mul = sortDir === "asc" ? 1 : -1;
  return [...games].sort((a, b) => {
    switch (sortKey) {
      case "date":
        // Prefer YYYY-MM-DD lexical sort to avoid timezone skew.
        return (
          mul *
          (gameDateYmd(a.game_date).localeCompare(gameDateYmd(b.game_date)) ||
            gameDateMs(a.game_date) - gameDateMs(b.game_date))
        );
      case "baseline":
        return cmpNum(
          numOrNull(a.p_home_baseline),
          numOrNull(b.p_home_baseline),
          mul,
        );
      case "user":
        return cmpNum(numOrNull(a.p_home_user), numOrNull(b.p_home_user), mul);
      case "delta":
        return cmpNum(
          numOrNull(a.__delta_user_vs_baseline ?? a.delta_user_vs_baseline),
          numOrNull(b.__delta_user_vs_baseline ?? b.delta_user_vs_baseline),
          mul,
        );
      case "ev":
        return cmpNum(numOrNull(a.ev), numOrNull(b.ev), mul);
      case "gap": {
        const gapA =
          (a.__baseline_p_home ?? a.p_home_baseline) != null &&
          a.offered_ml != null
            ? (a.__baseline_p_home ?? a.p_home_baseline) -
              americanToImplied(a.offered_ml)
            : null;
        const gapB =
          (b.__baseline_p_home ?? b.p_home_baseline) != null &&
          b.offered_ml != null
            ? (b.__baseline_p_home ?? b.p_home_baseline) -
              americanToImplied(b.offered_ml)
            : null;
        return cmpNum(numOrNull(gapA), numOrNull(gapB), mul);
      }
      default:
        return 0;
    }
  });
}

function describeWindow(w: SlateWindow | undefined, allMode: boolean): string {
  if (allMode)
    return "Showing every prediction row (past and future), ignoring the date window.";
  if (!w) return "";
  if (w.from == null && w.to == null)
    return "Showing all games with predictions.";
  const upcoming =
    w.upcoming_only !== false &&
    w.upcoming_only !== null &&
    w.upcoming_only !== undefined;
  const scope = upcoming
    ? "Upcoming games that are not final yet"
    : "Games in this window (including completed)";
  if (w.from && w.to && w.from === w.to) {
    return `${scope}, scheduled on ${fmtIsoDate(w.from)}.`;
  }
  return `${scope}, scheduled ${fmtIsoDate(w.from)} — ${fmtIsoDate(w.to)}.`;
}

type ConfidenceBadge = { label: string; title: string } | null;

function confidenceBadge(g: SlateGame): ConfidenceBadge {
  if (g.low_confidence !== true) return null;
  const cov = g.coverage == null ? null : Math.round(g.coverage * 100);
  const coverageText =
    cov == null ? "coverage unknown" : `${cov}% feature coverage`;
  const nanText =
    g.n_nan_features == null
      ? ""
      : ` · ${g.n_nan_features} missing feature${g.n_nan_features === 1 ? "" : "s"}`;
  return {
    label: "low confidence",
    title: `Prediction uses limited data (${coverageText}${nanText}). Common for early-season or just-scheduled games where rolling-window stats aren't available yet.`,
  };
}

type ProbStyle = "bar" | "number" | "gauge";

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="info-tip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="info-tip-trigger"
        aria-label="More information"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      {open && (
        <span role="tooltip" className="info-tip-pop">
          {text}
        </span>
      )}
    </span>
  );
}

function WinBar({
  pHome,
  probStyle,
}: {
  pHome: number | null | undefined;
  probStyle: ProbStyle;
}) {
  if (pHome == null || Number.isNaN(pHome)) {
    return <span className="cell-dash">—</span>;
  }
  const homeWin = pHome * 100;
  const awayWin = 100 - homeWin;
  const isHomeFav = homeWin > 50;

  if (probStyle === "number") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: isHomeFav ? "#e8e9f0" : "#8a8ca8",
          }}
        >
          {homeWin.toFixed(1)}%
        </span>
        <span className="win-bar-away-label">Away {awayWin.toFixed(1)}%</span>
      </div>
    );
  }

  if (probStyle === "gauge") {
    const angle = (homeWin / 100) * 180 - 90;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <svg width="48" height="26" viewBox="0 0 48 26" aria-hidden>
          <path
            d="M4 24 A20 20 0 0 1 44 24"
            fill="none"
            stroke="#2e3050"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M4 24 A20 20 0 0 1 44 24"
            fill="none"
            stroke={isHomeFav ? "#4ade80" : "#f87171"}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${(homeWin / 100) * 62.8} 62.8`}
          />
          <line
            x1="24"
            y1="24"
            x2={24 + 18 * Math.cos(((angle - 90) * Math.PI) / 180)}
            y2={24 + 18 * Math.sin(((angle - 90) * Math.PI) / 180)}
            stroke="#e8e9f0"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          {homeWin.toFixed(1)}%
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div className="win-bar-row">
        <div className="win-bar-track">
          <div
            className={`win-bar-fill ${isHomeFav ? "win-bar-fill--home" : "win-bar-fill--away"}`}
            style={{ width: `${homeWin}%` }}
          />
        </div>
        <span
          className={`win-bar-pct ${isHomeFav ? "win-bar-pct--strong" : "win-bar-pct--muted"}`}
        >
          {homeWin.toFixed(1)}%
        </span>
      </div>
      <span className="win-bar-away-label">Away {awayWin.toFixed(1)}%</span>
    </div>
  );
}

function researchNumericField(g: SlateGame, field: string): number | null {
  const v = (g as Record<string, unknown>)[field];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

function ResearchModelCell({
  g,
  meta,
  probStyle,
}: {
  g: SlateGame;
  meta: SlateResearchModel;
  probStyle: ProbStyle;
}) {
  const p = researchNumericField(g, meta.field_p_home);
  const mu =
    meta.field_mu_total != null && meta.field_mu_total !== ""
      ? researchNumericField(g, meta.field_mu_total)
      : null;
  if (p == null || Number.isNaN(p)) {
    return <span className="cell-dash">—</span>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <WinBar pHome={p} probStyle={probStyle} />
      {mu != null && !Number.isNaN(mu) && (
        <span
          className="muted small"
          style={{ fontSize: "0.6875rem" }}
          title={`${meta.label_short} — predicted game total`}
        >
          μΣ {fmtTotal(mu)}
        </span>
      )}
    </div>
  );
}

function MlCell({ ml }: { ml: number | null | undefined }) {
  if (ml == null || Number.isNaN(ml)) {
    return <span className="cell-dash">—</span>;
  }
  const n = Math.round(ml);
  const pos = n > 0;
  return (
    <span className={`ml-badge ${pos ? "ml-badge--pos" : "ml-badge--neg"}`}>
      {pos ? "+" : ""}
      {n}
    </span>
  );
}

const RECENT_MS = 24 * 60 * 60 * 1000; // 24 hours

const NOW_TICK_MS = 60_000;

// Shared clock store: getSnapshot must stay stable between ticks. Returning
// Date.now() directly from getSnapshot causes React #185 (infinite updates).
let clockSnapshot = Date.now();
let clockTimer: ReturnType<typeof setInterval> | null = null;
const clockListeners = new Set<() => void>();

function tickClock() {
  clockSnapshot = Date.now();
  for (const listener of clockListeners) listener();
}

function subscribeClock(onStoreChange: () => void) {
  clockListeners.add(onStoreChange);
  if (!clockTimer) {
    clockTimer = setInterval(tickClock, NOW_TICK_MS);
  }
  return () => {
    clockListeners.delete(onStoreChange);
    if (clockListeners.size === 0 && clockTimer) {
      clearInterval(clockTimer);
      clockTimer = null;
    }
  };
}

function getClockSnapshot() {
  return clockSnapshot;
}

function useNowMs(): number {
  return useSyncExternalStore(
    subscribeClock,
    getClockSnapshot,
    getClockSnapshot,
  );
}

function timeAgo(when: Date, nowMs: number): string {
  const diff = nowMs - when.getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function fmtStamp(when: Date): string {
  return when.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function UpdatedWrap({
  changedAt,
  children,
}: {
  changedAt: string | null | undefined;
  children: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);
  const nowMs = useNowMs();
  const when = changedAt ? new Date(changedAt) : null;
  const recent = when != null && nowMs - when.getTime() <= RECENT_MS;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 8,
        background: recent ? "rgba(34,211,238,0.07)" : "transparent",
        border: recent
          ? "1px solid rgba(34,211,238,0.28)"
          : "1px solid transparent",
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      {recent && (
        <span
          aria-label="recently updated"
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#22d3ee",
            boxShadow: "0 0 0 3px rgba(34,211,238,0.18)",
            animation: "pb-pulse 1.6s ease-in-out infinite",
          }}
        />
      )}
      {children}
      {hover && when && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: 6,
            zIndex: 50,
            background: "#1e1f30",
            border: "1px solid #2e3050",
            borderRadius: 7,
            padding: "7px 10px",
            whiteSpace: "nowrap",
            boxShadow: "0 8px 20px rgba(0,0,0,0.5)",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: recent ? "#22d3ee" : "#8a8ca8",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {recent
              ? `Just updated · ${timeAgo(when, nowMs)}`
              : `Updated ${timeAgo(when, nowMs)}`}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#c0c2d8",
              marginTop: 2,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtStamp(when)}
          </div>
        </div>
      )}
    </div>
  );
}

const TRACKED_MODEL_IDS = ["baseline", "dvoa_box_impact", "epm_style"] as const;
type TrackedModelId = (typeof TRACKED_MODEL_IDS)[number];

function changedAtFor(
  g: SlateGameDerived,
  modelId: string,
): string | null | undefined {
  switch (modelId) {
    case "baseline":
      return g.prediction_changed_at;
    case "dvoa_box_impact":
      return g.prediction_changed_at_dvoa_box_impact;
    case "epm_style":
      return g.prediction_changed_at_epm_style;
    default:
      return null;
  }
}

type LatestChangedMap = Record<TrackedModelId, string | null>;

function updatedModelsFor(
  g: SlateGameDerived,
  latest: LatestChangedMap,
): TrackedModelId[] {
  return TRACKED_MODEL_IDS.filter((id) => {
    const latestVal = latest[id];
    return latestVal != null && changedAtFor(g, id) === latestVal;
  });
}

/** The selected model's home win-probability for this row, used to keep the
 *  derived "Model ML" cell consistent with the chosen prediction. */
function selectedPHome(
  g: SlateGameDerived,
  modelId: string,
  researchModels: SlateResearchModel[],
): number | null | undefined {
  if (modelId === "user") return g.p_home_user;
  if (modelId.startsWith("research_")) {
    const rid = modelId.slice("research_".length);
    const rm = researchModels.find((m) => m.id === rid);
    if (!rm) return null;
    const v = (g as Record<string, unknown>)[rm.field_p_home];
    return typeof v === "number" ? v : null;
  }
  return g.__baseline_p_home ?? g.p_home_baseline;
}

/** Wraps a prediction cell so activating it (click / Enter / Space) shows that
 *  model's betting odds for the row. Renders children unwrapped (and
 *  non-interactive) when the model has no betting entry in the API response,
 *  so unavailable models can't be selected. */
function PredictionSelectButton({
  label,
  selected,
  available,
  onSelect,
  children,
}: {
  label: string;
  selected: boolean;
  available: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  if (!available) return <>{children}</>;
  return (
    <button
      type="button"
      className={`bet-pick${selected ? " bet-pick--active" : ""}`}
      aria-pressed={selected}
      title={
        selected
          ? `${label} betting odds shown for this game`
          : `Show ${label} betting odds for this game`
      }
      aria-label={
        selected
          ? `${label} betting odds shown for this game`
          : `Show ${label} betting odds for this game`
      }
      onClick={onSelect}
      style={{
        display: "block",
        width: "100%",
        textAlign: "inherit",
        font: "inherit",
        color: "inherit",
        cursor: "pointer",
        padding: "0.15rem 0.25rem",
        borderRadius: "0.4rem",
        background: selected ? "rgba(56, 189, 248, 0.10)" : "transparent",
        border: selected ? "1px solid #38bdf8" : "1px solid transparent",
      }}
    >
      {children}
    </button>
  );
}

function SlateUpcomingRow({
  g,
  showDate,
  probStyle,
  showYourModel,
  showDeltaPp,
  showMarketColumns,
  researchModels,
  bettingModels,
  selectedModelId,
  onSelectModel,
}: {
  g: SlateGameDerived;
  showDate: boolean;
  probStyle: ProbStyle;
  showYourModel: boolean;
  showDeltaPp: boolean;
  showMarketColumns: boolean;
  researchModels: SlateResearchModel[];
  bettingModels: SlateBettingModel[];
  selectedModelId: string;
  onSelectModel: (modelId: string) => void;
}) {
  const badge = confidenceBadge(g);
  const isLow = g.low_confidence === true;
  const baseline = g.__baseline_p_home ?? g.p_home_baseline;
  const delta = g.__delta_user_vs_baseline ?? g.delta_user_vs_baseline ?? null;
  const gapVal =
    baseline != null && g.offered_ml != null
      ? baseline - americanToImplied(g.offered_ml)
      : null;

  // Per-row betting model: which model's odds the Betting columns show.
  const activeMeta = resolveActiveBettingModel(selectedModelId, bettingModels);
  const bet = resolveBettingFields(g, activeMeta);
  const isBaselineActive = activeMeta.id === "baseline";
  const userBettingAvailable = bettingModels.some((m) => m.id === "user");
  const modelMlPHome = selectedPHome(g, activeMeta.id, researchModels);

  return (
    <tr className={isLow ? "slate-row-low" : undefined}>
      {showDate && <td className="muted small">{fmtGameDate(g.game_date)}</td>}
      <td>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span className="matchup-away">{g.away_team}</span>
          <span className="matchup-home">@ {g.home_team}</span>
          {badge && (
            <span className="badge-low-confidence" title={badge.title}>
              {badge.label}
            </span>
          )}
        </div>
      </td>
      <td>
        <UpdatedWrap changedAt={changedAtFor(g, "baseline")}>
          <PredictionSelectButton
            label="Baseline"
            selected={isBaselineActive}
            available
            onSelect={() => onSelectModel("baseline")}
          >
            <WinBar pHome={baseline} probStyle={probStyle} />
          </PredictionSelectButton>
        </UpdatedWrap>
      </td>
      {showYourModel && (
        <td>
          {g.p_home_user != null && !Number.isNaN(g.p_home_user) ? (
            <PredictionSelectButton
              label="My model"
              selected={activeMeta.id === "user"}
              available={userBettingAvailable}
              onSelect={() => onSelectModel("user")}
            >
              <WinBar pHome={g.p_home_user} probStyle={probStyle} />
            </PredictionSelectButton>
          ) : (
            <span className="cell-dash">—</span>
          )}
        </td>
      )}
      {showDeltaPp && (
        <td style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {delta == null ? (
            <span className="cell-dash">—</span>
          ) : (
            <span className={delta > 0 ? "delta-pos" : "delta-neg"}>
              {fmtDelta(delta)}
            </span>
          )}
        </td>
      )}
      {researchModels.map((rm, idx) => {
        const bid = `research_${rm.id}`;
        return (
          <td key={rm.id} className={idx === 0 ? "slate-td-border" : ""}>
            <UpdatedWrap changedAt={changedAtFor(g, rm.id)}>
              <PredictionSelectButton
                label={rm.label_short}
                selected={activeMeta.id === bid}
                available={bettingModels.some((m) => m.id === bid)}
                onSelect={() => onSelectModel(bid)}
              >
                <ResearchModelCell g={g} meta={rm} probStyle={probStyle} />
              </PredictionSelectButton>
            </UpdatedWrap>
          </td>
        );
      })}
      {showMarketColumns && (
        <>
          <td className="slate-td-border">
            <MlCell ml={g.offered_ml} />
          </td>
          <td>
            {g.offered_ml != null ? (
              <span className="muted small">
                {fmtProb(americanToImplied(g.offered_ml))}
              </span>
            ) : (
              <span className="cell-dash">—</span>
            )}
          </td>
          <td
            className={
              gapVal == null ? "" : gapVal > 0 ? "delta-pos" : "delta-neg"
            }
            style={{ fontWeight: 500 }}
          >
            {gapVal == null ? (
              <span className="cell-dash">—</span>
            ) : (
              `${gapVal > 0 ? "+" : ""}${(gapVal * 100).toFixed(1)}`
            )}
          </td>
        </>
      )}
      <td className="slate-td-border">
        <MlCell
          ml={(() => {
            const p = modelMlPHome;
            if (p == null || Number.isNaN(p) || p <= 0 || p >= 1) return null;
            const raw = p >= 0.5 ? -(p / (1 - p)) * 100 : ((1 - p) / p) * 100;
            return Math.round(raw);
          })()}
        />
      </td>
      <td
        className={
          bet.ev == null ? "" : bet.ev >= 0 ? "delta-pos" : "delta-neg"
        }
        style={{ fontWeight: bet.ev != null && bet.ev >= 0 ? 600 : undefined }}
      >
        {bet.ev == null ? <span className="cell-dash">—</span> : fmtEV(bet.ev)}
        {!isBaselineActive && (
          <span
            className="muted small"
            style={{ display: "block", marginTop: "0.1rem" }}
            title="Betting odds shown are for this model"
          >
            via {activeMeta.label}
          </span>
        )}
      </td>
      <td>
        {(() => {
          const info = edgeInfo(bet.edge);
          return (
            <>
              {info ? (
                <span
                  style={{
                    display: "inline-block",
                    padding: "0.1rem 0.4rem",
                    borderRadius: "0.3rem",
                    fontSize: "0.78rem",
                    background: info.bg,
                    color: info.fg,
                    fontWeight: 600,
                  }}
                >
                  {info.label}
                </span>
              ) : (
                <span className="cell-dash" style={{ fontSize: "0.78rem" }}>
                  —
                </span>
              )}
              {bet.bet && bet.kellyCapped != null && (
                <span
                  className="muted small"
                  style={{ display: "block", marginTop: "0.1rem" }}
                >
                  {fmtKelly(bet.kellyCapped)}
                </span>
              )}
            </>
          );
        })()}
      </td>
      <td>
        {g.opening_spread_home != null && (
          <span className="muted small">
            {fmtSpread(g.opening_spread_home)}
          </span>
        )}
        {bet.modelSpreadHome != null && (
          <span
            className="muted small"
            style={{ display: "block", marginTop: "0.15rem" }}
            title="Margin model predicted spread (home perspective). Negative = model sees home as favorite."
          >
            Model: {fmtSpread(bet.modelSpreadHome)}
          </span>
        )}
        {bet.atsGap != null && (
          <span
            className={`muted small ${bet.atsGap > 0 ? "delta-pos" : "delta-neg"}`}
            style={{
              display: "block",
              fontWeight: 600,
              marginTop: "0.05rem",
            }}
            title="ATS gap: how many points the model expects home to exceed the market spread. Positive = model expects home to cover."
          >
            {bet.atsGap > 0 ? "+" : ""}
            {bet.atsGap.toFixed(2)} ATS
          </span>
        )}
        {/* FE.P2: explicit missing-data state — the selected model doesn't
            produce a spread. Don't silently fall back to baseline. */}
        {!isBaselineActive && !activeMeta.has_spread && (
          <span
            className="muted small"
            style={{ display: "block", marginTop: "0.15rem" }}
            title="Not produced by this model"
          >
            Model spread: —
          </span>
        )}
        {(g.opening_total != null || bet.muTotal != null) && (
          <div
            className="muted small"
            style={{ marginTop: "0.35rem", fontSize: "0.6875rem" }}
          >
            O/U{" "}
            {g.opening_total != null
              ? fmtTotal(g.opening_total)
              : bet.muTotal != null
                ? fmtTotal(bet.muTotal)
                : "—"}
            {bet.muTotal != null && g.opening_total != null && (
              <span
                className="muted small"
                style={{ display: "block", marginTop: "0.15rem" }}
                title="Model total (home pts + away pts predicted by totals model)"
              >
                Model: {fmtTotal(bet.muTotal)}
              </span>
            )}
            {/* FE.P2: selected model produces no total — say so explicitly. */}
            {!isBaselineActive && !activeMeta.has_total && (
              <span
                className="muted small"
                style={{ display: "block", marginTop: "0.15rem" }}
                title="Not produced by this model"
              >
                Model: —
              </span>
            )}
            {bet.ouEdge != null && (
              <span
                className={bet.ouEdge > 0 ? "delta-pos" : "delta-neg"}
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginTop: "0.05rem",
                }}
                title="O/U gap: model total minus market total. Positive = model leans Over; negative = model leans Under."
              >
                {bet.ouEdge > 0 ? "▲" : "▼"} {bet.ouEdge > 0 ? "+" : ""}
                {bet.ouEdge.toFixed(2)} O/U
              </span>
            )}
          </div>
        )}
        {g.opening_spread_home == null &&
          bet.modelSpreadHome == null &&
          g.opening_total == null &&
          bet.muTotal == null &&
          // Suppress the bare dash when a "not produced by this model"
          // placeholder already fills the cell (FE.P2).
          !(!isBaselineActive && (!activeMeta.has_spread || !activeMeta.has_total)) && (
            <span className="cell-dash">—</span>
          )}
      </td>
    </tr>
  );
}

export function SlatePage() {
  const { user } = useAuth();
  const { slate: slatePrefs, updateSlate } = useDisplayPreferences();
  const leagueId = config.leagueId;
  const [slate, setSlate] = useState<SlateResponse | null>(null);
  // Per-row betting-model selection (game_id -> model_id), persisted per
  // league. Baseline rows are absent from the map (spec 007, per-row variant).
  const [rowBetSel, setRowBetSel] = useState<Record<string, string>>(() =>
    loadRowSelections(config.leagueId),
  );
  const [history, setHistory] = useState<PredictionHistoryResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [allMode, setAllMode] = useState(false);
  const [teamFilter, setTeamFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  /** Date column: ascending = next upcoming first; other columns default to descending. */
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [rangeStart, setRangeStart] = useState(() => localTodayYmd());
  const [rangeEnd, setRangeEnd] = useState("");
  const [displayOptsOpen, setDisplayOptsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [view, setView] = useState<SlateView>("upcoming");

  useEffect(() => {
    if (!user) return;
    const ctrl = new AbortController();
    queueMicrotask(() => {
      setLoading(true);
      setError(null);
      setHistoryError(null);
    });
    void Promise.allSettled([
      api.getSlate({
        idToken: user.idToken,
        signal: ctrl.signal,
        all: allMode || undefined,
      }),
      api.getPredictionHistory({
        idToken: user.idToken,
        signal: ctrl.signal,
      }),
    ])
      .then((results) => {
        const [slateRes, histRes] = results;
        if (slateRes.status === "fulfilled") setSlate(slateRes.value);
        else if (slateRes.reason?.name !== "AbortError")
          setError(String(slateRes.reason?.message ?? slateRes.reason));
        if (histRes.status === "fulfilled") setHistory(histRes.value);
        else if (histRes.reason?.name !== "AbortError")
          setHistoryError(String(histRes.reason?.message ?? histRes.reason));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [user, allMode]);

  const baselineExplain =
    slate?.baseline?.explain ??
    "Baseline is the best available league model produced by the pipeline. When available, its selected features and feature-group weights are shown here.";
  const baselineInfo = slate?.baseline?.info ?? null;

  const baselineWeightSummary = useMemo(() => {
    const fgw = (baselineInfo as Record<string, unknown> | null | undefined)
      ?.feature_group_weights;
    if (!fgw || typeof fgw !== "object") return null;
    const rows: { k: string; v: number }[] = [];
    for (const [k, v] of Object.entries(fgw as Record<string, unknown>)) {
      const n =
        typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
      if (!Number.isFinite(n)) continue;
      rows.push({ k, v: n });
    }
    const interesting = rows
      .filter((r) => Math.abs(r.v - 1.0) >= 0.15)
      .sort((a, b) => Math.abs(b.v - 1.0) - Math.abs(a.v - 1.0))
      .slice(0, 4);
    if (!interesting.length) return null;
    return interesting.map((r) => `${r.k}: ${r.v.toFixed(2)}×`).join(" · ");
  }, [baselineInfo]);

  const windowText = useMemo(
    () => describeWindow(slate?.window, allMode),
    [slate?.window, allMode],
  );

  const bounds = useMemo(() => catalogDateBounds(slate?.games), [slate?.games]);

  const dateRangeDefaults = useMemo(() => {
    if (!bounds) return null;
    return {
      start: clampYmd(localTodayYmd(), bounds.min, bounds.max),
      end: bounds.max,
    };
  }, [bounds]);

  useEffect(() => {
    if (!dateRangeDefaults) return;
    queueMicrotask(() => {
      setRangeEnd(dateRangeDefaults.end);
      setRangeStart(dateRangeDefaults.start);
    });
  }, [slate?.generated_at, allMode, dateRangeDefaults]);

  const endForFilter = rangeEnd || bounds?.max || "";
  const startForFilter = rangeStart;

  const slateGames = slate?.games;
  const displayedGames = useMemo(() => {
    if (!slateGames?.length) return [];
    const derived: SlateGameDerived[] = slateGames.map((g) => {
      const baseline = g.p_home_baseline;
      const delta = g.delta_user_vs_baseline ?? null;
      return {
        ...g,
        __baseline_p_home: baseline,
        __delta_user_vs_baseline: delta,
      };
    });

    const filtered = derived
      .filter((g) => teamFilterMatch(g, teamFilter))
      .filter((g) => dateRangeMatch(g, startForFilter, endForFilter));
    return sortGames(filtered, sortKey, sortDir);
  }, [slateGames, teamFilter, sortKey, sortDir, startForFilter, endForFilter]);

  const baselineLabel = "Baseline";

  const hasMarket = useMemo(
    () => displayedGames.some((g) => g.offered_ml != null),
    [displayedGames],
  );

  const showMarketColumns =
    showAllColumns || !slatePrefs.hide_empty_market || hasMarket;

  // Per-model "latest score run" timestamps across the slate. A row is
  // considered "updated" if its column for any of the three models equals
  // that model's max.
  const latestChangedAt = useMemo<LatestChangedMap>(() => {
    const out: LatestChangedMap = {
      baseline: null,
      dvoa_box_impact: null,
      epm_style: null,
    };
    for (const g of displayedGames) {
      for (const id of TRACKED_MODEL_IDS) {
        const t = changedAtFor(g, id);
        const cur = out[id];
        if (t && (cur == null || t > cur)) out[id] = t;
      }
    }
    return out;
  }, [displayedGames]);

  const [showOnlyUpdated, setShowOnlyUpdated] = useState<boolean>(false);

  const visibleGames = useMemo(
    () =>
      showOnlyUpdated
        ? displayedGames.filter(
            (g) => updatedModelsFor(g, latestChangedAt).length > 0,
          )
        : displayedGames,
    [displayedGames, latestChangedAt, showOnlyUpdated],
  );

  const updatedCount = useMemo(
    () =>
      displayedGames.filter(
        (g) => updatedModelsFor(g, latestChangedAt).length > 0,
      ).length,
    [displayedGames, latestChangedAt],
  );

  const researchModels = slate?.research_models ?? [];
  const bettingModels = useMemo(
    () => resolveBettingModels(slate?.betting_models),
    [slate?.betting_models],
  );

  const handleSelectModel = (gameId: string, modelId: string) => {
    setRowBetSel((prev) => {
      const next = { ...prev };
      if (modelId === "baseline") delete next[gameId];
      else next[gameId] = modelId;
      // Prune to the current slate on every change so the persisted map
      // doesn't accumulate stale game ids over time.
      const pruned = slateGames
        ? pruneRowSelections(
            next,
            slateGames.map((g) => g.game_id),
          )
        : next;
      saveRowSelections(leagueId, pruned);
      return pruned;
    });
  };

  const dateGroups = useMemo(() => {
    if (!slatePrefs.group_by_date) return null;
    const order: string[] = [];
    const seen = new Set<string>();
    for (const g of visibleGames) {
      const label = fmtGameDate(g.game_date);
      if (!seen.has(label)) {
        seen.add(label);
        order.push(label);
      }
    }
    return order.map((date) => ({
      date,
      games: visibleGames.filter((g) => fmtGameDate(g.game_date) === date),
    }));
  }, [visibleGames, slatePrefs.group_by_date]);

  const baseColCount = slatePrefs.group_by_date ? 1 : 2;
  const userModelColCount =
    (slatePrefs.show_your_model ? 1 : 0) + (slatePrefs.show_delta_pp ? 1 : 0);
  const leaguePredictionColSpan = 1 + userModelColCount + researchModels.length;
  const totalTableCols =
    baseColCount + leaguePredictionColSpan + (showMarketColumns ? 3 : 0) + 4;

  const windowChipText = useMemo(() => {
    if (allMode) return "All predictions · past and future";
    const w = slate?.window;
    if (!w) return "";
    if (w.from && w.to) {
      const upcoming =
        w.upcoming_only !== false &&
        w.upcoming_only !== null &&
        w.upcoming_only !== undefined;
      const prefix = upcoming ? "Upcoming" : "Window";
      return `${prefix} · ${fmtIsoDate(w.from)} — ${fmtIsoDate(w.to)}`;
    }
    return "Predictions";
  }, [slate?.window, allMode]);

  const onSortClick = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "asc" : "desc");
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "";

  if (!user) {
    return (
      <section className="page">
        <h1>Predictions</h1>
        <p className="muted">
          Sign in with Google to see upcoming games and recent results: baseline
          probabilities and, if you&apos;ve trained one, your custom model
          alongside them.
        </p>
        <div className="card legal-callout" style={{ marginTop: "1rem" }}>
          <h2 style={{ marginTop: 0 }}>Responsible gaming</h2>
          <p className="muted" style={{ marginBottom: "0.5rem" }}>
            <strong>DISCLAIMER:</strong> This site is 100% for entertainment
            purposes only and does not involve real money betting or prizes.
          </p>
          <p className="muted small" style={{ marginTop: 0 }}>
            Sports betting is regulated and permitted only in select U.S.
            states. This content is intended solely for individuals of legal
            betting age (21+). Availability varies by location. No guarantees of
            outcomes are made. Please check your local laws and gamble
            responsibly.
          </p>
          <p className="muted small" style={{ marginBottom: 0 }}>
            Gambling problem? Call <strong>1-800-GAMBLER</strong>. You must be{" "}
            <strong>18+</strong> to use this site.
          </p>
          <p
            className="muted small"
            style={{ marginTop: "0.75rem", marginBottom: 0 }}
          >
            <Link to="/responsible-gaming">Responsible gaming</Link> ·{" "}
            <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="page">
      <div className="slate-page-head">
        <div className="slate-page-head-main">
          <h1>Predictions</h1>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.625rem",
              flexWrap: "wrap",
            }}
          >
            {windowChipText && (
              <span className="slate-window-chip">{windowChipText}</span>
            )}
            {slate && (
              <span className="slate-generated-meta">
                Generated {new Date(slate.generated_at).toLocaleString()}
                {slate.has_user_model
                  ? " · baseline vs. your model"
                  : " · baseline only"}
              </span>
            )}
          </div>
          {slate && (
            <div className="muted small" style={{ marginTop: "0.5rem" }}>
              <strong>Baseline</strong> is the best available league model
              produced by the pipeline.{" "}
              {baselineWeightSummary ? (
                <>
                  <span style={{ display: "block", marginTop: "0.25rem" }}>
                    Baseline weights: {baselineWeightSummary}
                  </span>
                </>
              ) : null}
            </div>
          )}
        </div>
        <button
          type="button"
          className="slate-info-btn"
          aria-expanded={infoOpen}
          onClick={() => setInfoOpen((o) => !o)}
        >
          <span>How this works</span>
          <span className="slate-info-chevron" aria-hidden>
            ▾
          </span>
        </button>
      </div>

      <div
        className="slate-view-tabs"
        role="tablist"
        aria-label="Predictions view"
      >
        <button
          type="button"
          role="tab"
          aria-selected={view === "upcoming"}
          className={`slate-view-tab${view === "upcoming" ? " active" : ""}`}
          onClick={() => setView("upcoming")}
        >
          Upcoming
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "results"}
          className={`slate-view-tab${view === "results" ? " active" : ""}`}
          onClick={() => setView("results")}
        >
          Results
        </button>
      </div>

      {view === "results" && <ResultsView />}

      {view === "upcoming" && (
        <>
          {infoOpen && (
            <div className="slate-info-panel">
              <div className="slate-info-grid">
                <div>
                  <strong>Scope</strong> — {windowText}
                </div>
                <div>
                  <strong>Upcoming</strong> — predicted probability the home
                  team wins, for not-yet-final games in the server&apos;s
                  rolling window (UTC).
                </div>
                <div>
                  <strong>Baseline</strong> — league model. If you&apos;ve
                  trained a Model Lab run, <em>Your model</em> and Δ (percentage
                  points) appear.
                </div>
                {researchModels.length > 0 ? (
                  <div>
                    <strong>Research models</strong> — separate tracked
                    predictions (
                    {researchModels.map((m) => m.label_short).join(", ")})
                    shipped alongside the baseline pipeline model; titles match
                    table headers.
                  </div>
                ) : null}
                <div>
                  <strong>Market columns</strong> — populate when opening lines
                  are available from the pipeline. Schedule-only rows usually
                  show —.
                </div>
                <div>
                  <strong>Model spread / O/U</strong> — appears when the model
                  bundle includes margin and totals outputs joined at score
                  time.
                </div>
                <div>
                  <strong>Recent results</strong> — completed games from the
                  current calendar year only; see the section below the slate
                  table.
                </div>
              </div>
            </div>
          )}

          {loading && <p className="muted">Loading…</p>}
          {error && <p className="error">{error}</p>}

          {slate && (slate.games?.length ?? 0) === 0 && (
            <p className="muted">No games scheduled in this window.</p>
          )}

          {slate && (slate.games?.length ?? 0) > 0 && (
            <>
              <div className="slate-filters">
                <label className="field slate-team-filter">
                  <span>Team</span>
                  <div className="slate-search-wrap">
                    <span className="slate-search-icon" aria-hidden>
                      ⌕
                    </span>
                    <input
                      type="search"
                      placeholder="Filter by team…"
                      value={teamFilter}
                      onChange={(e) => setTeamFilter(e.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                </label>
                {bounds && (
                  <div className="slate-date-range">
                    <label className="field">
                      <span>From</span>
                      <input
                        type="date"
                        value={rangeStart}
                        min={bounds.min}
                        max={bounds.max}
                        onChange={(e) => setRangeStart(e.target.value)}
                      />
                    </label>
                    <label className="field">
                      <span>To</span>
                      <input
                        type="date"
                        value={rangeEnd || bounds.max}
                        min={bounds.min}
                        max={bounds.max}
                        onChange={(e) => setRangeEnd(e.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="slate-filter-reset"
                      onClick={() => {
                        if (!dateRangeDefaults) return;
                        setRangeEnd(dateRangeDefaults.end);
                        setRangeStart(dateRangeDefaults.start);
                        setTeamFilter("");
                      }}
                      disabled={
                        !dateRangeDefaults ||
                        (rangeStart === dateRangeDefaults.start &&
                          (rangeEnd || bounds.max) === dateRangeDefaults.end &&
                          teamFilter.trim() === "")
                      }
                    >
                      Reset
                    </button>
                  </div>
                )}
                <div className="slate-filters-extras">
                  <button
                    type="button"
                    className="slate-display-inline-btn"
                    onClick={() => setDisplayOptsOpen(true)}
                  >
                    Display options…
                  </button>
                  <label className="slate-checkbox">
                    <input
                      type="checkbox"
                      checked={showAllColumns}
                      onChange={(e) => setShowAllColumns(e.target.checked)}
                    />
                    Show all columns
                  </label>
                  <label className="slate-checkbox">
                    <input
                      type="checkbox"
                      checked={allMode}
                      onChange={(e) => setAllMode(e.target.checked)}
                    />
                    Show all predictions
                  </label>
                </div>
              </div>

              {(teamFilter.trim() !== "" ||
                displayedGames.length !== slate.games.length) && (
                <p className="muted small" style={{ marginTop: "-0.35rem" }}>
                  {displayedGames.length} of {slate.games.length} games
                </p>
              )}

              {displayedGames.length === 0 ? (
                <p className="muted">No games match these filters.</p>
              ) : (
                <div className="slate-table-shell">
                  <div className="slate-table-scroll">
                    <table
                      className={`slate-table slate-table--density-${slatePrefs.table_density}`}
                    >
                      <thead>
                        <tr>
                          <td
                            colSpan={baseColCount}
                            className="slate-colgroup-label slate-colgroup-label--spacer"
                          >
                            {" "}
                          </td>
                          <td
                            colSpan={leaguePredictionColSpan}
                            className="slate-colgroup-label slate-colgroup-label--model"
                          >
                            {formatLeagueLabel(slate.league_id)} predictions
                          </td>
                          {showMarketColumns && (
                            <td
                              colSpan={3}
                              className="slate-colgroup-label slate-colgroup-label--market"
                            >
                              Market
                            </td>
                          )}
                          <td
                            colSpan={4}
                            className="slate-colgroup-label slate-colgroup-label--bet"
                          >
                            Betting
                          </td>
                        </tr>
                        <tr>
                          {!slatePrefs.group_by_date && (
                            <th
                              scope="col"
                              className="slate-th"
                              style={{ width: 110 }}
                              {...(sortKey === "date"
                                ? {
                                    "aria-sort":
                                      sortDir === "asc"
                                        ? "ascending"
                                        : "descending",
                                  }
                                : {})}
                            >
                              <button
                                type="button"
                                className="th-sort"
                                onClick={() => onSortClick("date")}
                                title="Sort by game date (ascending: next upcoming first)"
                              >
                                Date
                                <span className="th-sort-dir" aria-hidden>
                                  {sortIndicator("date")}
                                </span>
                              </button>
                            </th>
                          )}
                          <th scope="col" className="slate-th">
                            Matchup
                          </th>
                          <th scope="col" className="slate-th slate-th--border">
                            <span className="th-tip-wrap">
                              <button
                                type="button"
                                className="th-sort"
                                onClick={() => onSortClick("baseline")}
                                title="Sort by baseline home win probability"
                              >
                                Baseline
                                <span className="th-sort-dir" aria-hidden>
                                  {sortIndicator("baseline")}
                                </span>
                              </button>
                              <InfoTooltip text="Best available league baseline probability." />
                            </span>
                          </th>
                          {slatePrefs.show_your_model && (
                            <th
                              scope="col"
                              className="slate-th"
                              {...(sortKey === "user"
                                ? {
                                    "aria-sort":
                                      sortDir === "asc"
                                        ? "ascending"
                                        : "descending",
                                  }
                                : {})}
                            >
                              <span className="th-tip-wrap">
                                <button
                                  type="button"
                                  className="th-sort"
                                  onClick={() => onSortClick("user")}
                                  title="Sort by your model home win probability"
                                >
                                  Your model
                                  <span className="th-sort-dir" aria-hidden>
                                    {sortIndicator("user")}
                                  </span>
                                </button>
                                <InfoTooltip text="Win% from your trained Model Lab run. Requires a completed run." />
                              </span>
                            </th>
                          )}
                          {slatePrefs.show_delta_pp && (
                            <th
                              scope="col"
                              className="slate-th"
                              {...(sortKey === "delta"
                                ? {
                                    "aria-sort":
                                      sortDir === "asc"
                                        ? "ascending"
                                        : "descending",
                                  }
                                : {})}
                            >
                              <span className="th-tip-wrap">
                                <button
                                  type="button"
                                  className="th-sort"
                                  onClick={() => onSortClick("delta")}
                                  title={`Sort by delta (your model minus ${baselineLabel.toLowerCase()} baseline)`}
                                >
                                  Δ (pp)
                                  <span className="th-sort-dir" aria-hidden>
                                    {sortIndicator("delta")}
                                  </span>
                                </button>
                                <InfoTooltip
                                  text={`Difference in percentage points between your model and the selected baseline (${baselineLabel.toLowerCase()}).`}
                                />
                              </span>
                            </th>
                          )}
                          {researchModels.map((rm, idx) => (
                            <th
                              key={`h-${rm.id}`}
                              scope="col"
                              className={`slate-th${idx === 0 ? " slate-th--border" : ""}${researchHeaderWraps(rm.label_short) ? " slate-th--wrap" : ""}`}
                            >
                              <span
                                className={`th-tip-wrap${researchHeaderWraps(rm.label_short) ? " th-tip-wrap--multiline" : ""}`}
                              >
                                <span title={rm.label_short}>
                                  {formatResearchModelHeader(rm.label_short)}
                                </span>
                                {rm.explain ? (
                                  <InfoTooltip text={rm.explain} />
                                ) : null}
                              </span>
                            </th>
                          ))}
                          {showMarketColumns && (
                            <>
                              <th
                                scope="col"
                                className="slate-th slate-th--border"
                              >
                                <span className="th-tip-wrap">
                                  Market ML
                                  <InfoTooltip text="Opening moneyline from the market. Requires pipeline data." />
                                </span>
                              </th>
                              <th scope="col" className="slate-th">
                                <span className="th-tip-wrap">
                                  Implied%
                                  <InfoTooltip text="Market-implied win probability from the opening moneyline." />
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="slate-th"
                                {...(sortKey === "gap"
                                  ? {
                                      "aria-sort":
                                        sortDir === "asc"
                                          ? "ascending"
                                          : "descending",
                                    }
                                  : {})}
                              >
                                <span className="th-tip-wrap">
                                  <button
                                    type="button"
                                    className="th-sort"
                                    onClick={() => onSortClick("gap")}
                                    title="Sort by model vs market gap (model probability minus book-implied probability)."
                                  >
                                    Gap
                                    <span className="th-sort-dir" aria-hidden>
                                      {sortIndicator("gap")}
                                    </span>
                                  </button>
                                  <InfoTooltip text="Difference between model probability and market-implied probability." />
                                </span>
                              </th>
                            </>
                          )}
                          <th scope="col" className="slate-th slate-th--border">
                            <span className="th-tip-wrap">
                              Model ML
                              <InfoTooltip text="Model moneyline — converted from win probability." />
                            </span>
                          </th>
                          <th
                            scope="col"
                            className="slate-th"
                            {...(sortKey === "ev"
                              ? {
                                  "aria-sort":
                                    sortDir === "asc"
                                      ? "ascending"
                                      : "descending",
                                }
                              : {})}
                          >
                            <span className="th-tip-wrap">
                              <button
                                type="button"
                                className="th-sort"
                                onClick={() => onSortClick("ev")}
                                title="Sort by expected value per $1 staked on home team"
                              >
                                EV / $1
                                <span className="th-sort-dir" aria-hidden>
                                  {sortIndicator("ev")}
                                </span>
                              </button>
                              <InfoTooltip text="Expected value in dollars per $1 staked on the home side." />
                            </span>
                          </th>
                          <th scope="col" className="slate-th">
                            <span className="th-tip-wrap">
                              Edge / Stake
                              <InfoTooltip text="Edge classification and suggested bankroll stake (quarter-Kelly, capped at 2%)." />
                            </span>
                          </th>
                          <th scope="col" className="slate-th">
                            <span className="th-tip-wrap">
                              Spread / O/U
                              <InfoTooltip text="Opening spread, model spread, ATS gap, totals line, model total, and O/U edge when available." />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {slatePrefs.group_by_date && dateGroups
                          ? dateGroups.map(({ date, games }) => (
                              <Fragment key={date}>
                                <tr className="slate-date-group-row">
                                  <td colSpan={totalTableCols}>{date}</td>
                                </tr>
                                {games.map((g) => (
                                  <SlateUpcomingRow
                                    key={g.game_id}
                                    g={g}
                                    showDate={false}
                                    probStyle={slatePrefs.prob_style}
                                    showYourModel={slatePrefs.show_your_model}
                                    showDeltaPp={slatePrefs.show_delta_pp}
                                    showMarketColumns={showMarketColumns}
                                    researchModels={researchModels}
                                    bettingModels={bettingModels}
                                    selectedModelId={
                                      rowBetSel[g.game_id] ?? "baseline"
                                    }
                                    onSelectModel={(id) =>
                                      handleSelectModel(g.game_id, id)
                                    }
                                  />
                                ))}
                              </Fragment>
                            ))
                          : visibleGames.map((g) => (
                              <SlateUpcomingRow
                                key={g.game_id}
                                g={g}
                                showDate
                                probStyle={slatePrefs.prob_style}
                                showYourModel={slatePrefs.show_your_model}
                                showDeltaPp={slatePrefs.show_delta_pp}
                                showMarketColumns={showMarketColumns}
                                researchModels={researchModels}
                                bettingModels={bettingModels}
                                selectedModelId={
                                  rowBetSel[g.game_id] ?? "baseline"
                                }
                                onSelectModel={(id) =>
                                  handleSelectModel(g.game_id, id)
                                }
                              />
                            ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="slate-table-footer">
                    <span className="slate-table-footer-meta">
                      {displayedGames.length} games · Market data available for{" "}
                      {
                        displayedGames.filter((x) => x.offered_ml != null)
                          .length
                      }
                    </span>
                    <div className="slate-legend">
                      <span className="slate-legend-item">
                        <span
                          className="slate-legend-swatch slate-legend-swatch--home"
                          aria-hidden
                        />
                        Home favored
                      </span>
                      <span className="slate-legend-item">
                        <span
                          className="slate-legend-swatch slate-legend-swatch--away"
                          aria-hidden
                        />
                        Away favored
                      </span>
                      <span className="slate-legend-item slate-legend-low-text">
                        <span
                          className="slate-legend-swatch slate-legend-swatch--low"
                          aria-hidden
                        />
                        Low confidence
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <h2 className="slate-section-title slate-section-title--follow">
            Recent results
          </h2>
          <p className="muted">
            Baseline pick (home win if probability ≥ 50%) vs actual outcome.
            Scoped to the current UTC calendar year; older seasons are omitted.
          </p>
          {historyError && <p className="error">{historyError}</p>}
          {history && (
            <p className="muted">
              {history.summary.n_games === 0 ? (
                <>
                  No completed games in the predictions file for this year yet.
                </>
              ) : (
                <>
                  This year: {history.summary.n_correct} /{" "}
                  {history.summary.n_games} correct (
                  {history.summary.accuracy == null
                    ? "—"
                    : `${(history.summary.accuracy * 100).toFixed(1)}%`}
                  ). Showing up to {history.filter?.row_limit ?? "—"} most
                  recent.
                </>
              )}
            </p>
          )}
          {history && (history.games?.length ?? 0) > 0 && (
            <div className="slate-table-shell">
              <div className="slate-table-scroll">
                <table className="slate-table">
                  <thead>
                    <tr>
                      <th className="slate-th">Date</th>
                      <th className="slate-th">Matchup</th>
                      <th className="slate-th">P(home) baseline</th>
                      <th className="slate-th">Home won</th>
                      <th className="slate-th">Predicted home</th>
                      <th className="slate-th">Correct</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.games.map((g: PredictionHistoryGame) => (
                      <tr key={g.league_game_id}>
                        <td>{fmtGameDate(g.game_date)}</td>
                        <td>
                          <span className="matchup-away">{g.away_team_id}</span>{" "}
                          <span className="matchup-home">
                            @ {g.home_team_id}
                          </span>
                        </td>
                        <td>{fmtProb(g.p_home_base)}</td>
                        <td>
                          {g.home_win_actual == null
                            ? "—"
                            : g.home_win_actual
                              ? "Yes"
                              : "No"}
                        </td>
                        <td>
                          {g.predicted_home_win == null
                            ? "—"
                            : g.predicted_home_win
                              ? "Yes"
                              : "No"}
                        </td>
                        <td>
                          {g.correct == null ? "—" : g.correct ? "Yes" : "No"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {displayOptsOpen && (
            <button
              type="button"
              className="slate-display-backdrop"
              aria-label="Close display options"
              onClick={() => setDisplayOptsOpen(false)}
            />
          )}
          <button
            type="button"
            className={`slate-display-tab${displayOptsOpen ? " slate-display-tab--hidden" : ""}`}
            onClick={() => setDisplayOptsOpen(true)}
            title="Display options"
          >
            <span className="slate-display-tab-icon" aria-hidden>
              ⚙
            </span>
            <span className="slate-display-tab-label">Display</span>
          </button>
          <aside
            className={`slate-display-panel${displayOptsOpen ? " is-open" : ""}`}
            aria-hidden={!displayOptsOpen}
          >
            <div className="slate-display-panel-head">
              <h2 className="slate-display-panel-title">Display options</h2>
              <button
                type="button"
                className="slate-display-panel-close"
                onClick={() => setDisplayOptsOpen(false)}
                aria-label="Close display options"
              >
                ×
              </button>
            </div>
            <div className="slate-display-panel-body">
              <details>
                <summary className="muted small">
                  What is the baseline model?
                </summary>
                <div className="muted small" style={{ marginTop: "0.5rem" }}>
                  <p style={{ marginTop: 0 }}>{baselineExplain}</p>
                  {baselineInfo && typeof baselineInfo === "object" ? (
                    <pre
                      className="muted small"
                      style={{ whiteSpace: "pre-wrap", margin: "0.75rem 0 0" }}
                    >
                      {JSON.stringify(baselineInfo, null, 2)}
                    </pre>
                  ) : (
                    <p style={{ marginBottom: 0 }}>
                      No baseline feature selection/weight info has been
                      published yet.
                    </p>
                  )}
                </div>
              </details>
              <div className="slate-display-row">
                <span className="slate-display-label">Win% display</span>
                <div
                  className="slate-segment"
                  role="group"
                  aria-label="Win percentage display style"
                >
                  {(["bar", "number", "gauge"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={
                        slatePrefs.prob_style === s ? "is-active" : undefined
                      }
                      onClick={() => updateSlate({ prob_style: s })}
                    >
                      {s === "bar"
                        ? "Bar + %"
                        : s === "number"
                          ? "Number only"
                          : "Gauge"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="slate-display-row">
                <span className="slate-display-label">Table density</span>
                <div
                  className="slate-segment"
                  role="group"
                  aria-label="Table density"
                >
                  {(["compact", "default", "relaxed"] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={
                        slatePrefs.table_density === d ? "is-active" : undefined
                      }
                      onClick={() => updateSlate({ table_density: d })}
                    >
                      {d === "compact"
                        ? "Compact"
                        : d === "default"
                          ? "Default"
                          : "Relaxed"}
                    </button>
                  ))}
                </div>
              </div>
              <label className="slate-display-toggle">
                <input
                  type="checkbox"
                  checked={slatePrefs.hide_empty_market}
                  onChange={(e) =>
                    updateSlate({ hide_empty_market: e.target.checked })
                  }
                />
                <span>Hide empty market columns</span>
              </label>
              <label className="slate-display-toggle">
                <input
                  type="checkbox"
                  checked={slatePrefs.group_by_date}
                  onChange={(e) =>
                    updateSlate({ group_by_date: e.target.checked })
                  }
                />
                <span>Group rows by date</span>
              </label>
              <div className="slate-display-row">
                <span className="slate-display-label">Columns</span>
                <label className="slate-display-toggle">
                  <input
                    type="checkbox"
                    checked={slatePrefs.show_your_model}
                    onChange={(e) =>
                      updateSlate({ show_your_model: e.target.checked })
                    }
                  />
                  <span>Your model</span>
                </label>
                <label className="slate-display-toggle">
                  <input
                    type="checkbox"
                    checked={slatePrefs.show_delta_pp}
                    onChange={(e) =>
                      updateSlate({ show_delta_pp: e.target.checked })
                    }
                  />
                  <span>Δ (percentage points)</span>
                </label>
              </div>
              <label className="slate-display-toggle">
                <input
                  type="checkbox"
                  checked={showOnlyUpdated}
                  onChange={(e) => setShowOnlyUpdated(e.target.checked)}
                />
                <span>
                  Show only updated predictions{" "}
                  <span className="muted small">
                    ({updatedCount} of {displayedGames.length})
                  </span>
                </span>
              </label>
              <p className="muted small slate-display-foot">
                Display settings sync to your account when signed in; this
                device keeps a local cache if the network is unavailable.
              </p>
            </div>
          </aside>
        </>
      )}
    </section>
  );
}
